<template>
  <b-row class="mb-0" :class="{ 'leaves-reg-root--employee': viewMode === 'my' }">
    <b-col lg="12" md="12" class="mb-0">
      <b-card
        id="regularization-section"
        no-body
        class="shadow-sm rounded-lg overflow-hidden leaves-reg-card"
        :class="{ 'leaves-reg-card--employee-chrome': viewMode === 'my' }"
      >
        <b-card-body>
          <div class="d-flex justify-content-between align-items-center border-bottom leaves-reg-header flex-wrap">
            <h5 class="mb-0 font-weight-bold leaves-reg-title">Leaves & Regularization</h5>
            <div class="d-flex align-items-center flex-wrap leaves-reg-toolbar" style="gap: 0.25rem;">
              <!-- Combined download menu when both exports are available (avoids twin Download icons on mobile) -->
              <b-dropdown
                v-if="showLeavesDownloadButton && showDownloadButton"
                size="sm"
                variant="outline-success"
                right
                no-caret
                class="leaves-download-dropdown"
                aria-label="Download Excel"
              >
                <template #button-content>
                  <feather-icon icon="DownloadIcon" size="14" />
                  <span class="ml-50 d-none d-md-inline">Download</span>
                </template>
                <b-dropdown-item @click="$emit('download-leaves-excel')">
                  Leaves (Excel)
                </b-dropdown-item>
                <b-dropdown-item @click="$emit('download-excel')">
                  Regularization (Excel)
                </b-dropdown-item>
              </b-dropdown>
              <b-button
                v-else-if="showLeavesDownloadButton"
                size="sm"
                variant="outline-success"
                class="leaves-toolbar-icon-btn"
                @click="$emit('download-leaves-excel')"
                aria-label="Download Leaves (Excel)"
              >
                <feather-icon icon="DownloadIcon" size="14" />
                <span class="ml-50 d-none d-md-inline">Leaves</span>
              </b-button>
              <b-button
                v-else-if="showDownloadButton"
                size="sm"
                variant="outline-success"
                class="leaves-toolbar-icon-btn"
                @click="$emit('download-excel')"
                aria-label="Download Regularization (Excel)"
              >
                <feather-icon icon="DownloadIcon" size="14" />
                <span class="ml-50 d-none d-md-inline">Regularization</span>
              </b-button>
              <b-button
                v-if="hasActiveFilters"
                size="sm"
                variant="outline-secondary"
                class="leaves-toolbar-icon-btn"
                @click="$emit('clear-filters')"
                aria-label="Clear Filters"
              >
                <feather-icon icon="XIcon" size="14" />
                <span class="ml-50 d-none d-md-inline">Clear</span>
              </b-button>
              <b-button
                size="sm"
                variant="outline-primary"
                :class="['leaves-filters-btn', 'leaves-toolbar-icon-btn', { 'leaves-filters-btn--active': hasActiveFilters }]"
                @click="$emit('open-filter-modal')"
                aria-label="Open filters"
              >
                <feather-icon icon="FilterIcon" size="14" />
                <span class="ml-50 d-none d-md-inline">Filters</span>
              </b-button>
            </div>
          </div>
          
          <b-tabs
            :value="activeTab"
            pills
            content-class="mt-0 leaves-reg-tab-content"
            nav-class="leaves-reg-tabs mb-50 rounded border"
            @input="$emit('update:activeTab', $event)"
          >
            <!-- Leaves Tab -->
            <b-tab>
              <template #title>
                Leaves
                <b-badge
                  v-if="leavesData.length > 0"
                  :variant="statusFilter === 'pending' ? 'warning' : 'secondary'"
                  pill
                  class="ml-1 leaves-count-badge"
                >{{ leavesData.length }}</b-badge>
                <b-badge
                  v-if="statusFilter === 'pending'"
                  variant="warning"
                  pill
                  class="ml-1 leaves-pending-chip"
                >Pending</b-badge>
              </template>
              
              <template v-if="leavesData.length > 0">
                <!-- Mobile/tablet rows (≤991): Bootstrap list-group — no custom flex card stack -->
                <b-list-group
                  flush
                  class="d-lg-none leaves-mobile-list-group border-0 bg-transparent"
                >
                  <b-list-group-item
                    v-for="(item, idx) in displayedLeavesData"
                    :key="leaveMobileKey(item, idx)"
                    class="leaves-mobile-row px-2 py-1"
                  >
                    <div class="d-flex align-items-center justify-content-between leaves-mobile-row__inner">
                      <div class="leaves-mobile-row__main min-w-0 flex-grow-1 mr-2">
                        <div
                          v-if="viewMode === 'team' || viewMode === 'org'"
                          class="leaves-mobile-row__name text-truncate small font-weight-bold text-body mb-0"
                        >
                          {{ employeeLabel(item) }}
                        </div>
                        <div class="d-flex align-items-center flex-wrap leaves-mobile-row__meta">
                          <span
                            class="leaves-mobile-row__date small text-body"
                            :class="{ 'font-weight-bold': viewMode === 'my' }"
                          >{{ viewMode === 'my' ? leaveMobilePrimary(item) : leaveDateRange(item) }}</span>
                          <b-badge
                            v-if="isLeaveRow(item)"
                            variant="light"
                            class="leave-type-chip ml-1"
                          >{{ item.issue_type }}</b-badge>
                          <b-badge
                            v-else
                            variant="light"
                            class="reg-issue-chip ml-1"
                            :class="'reg-issue--' + getIssueTypeVariant(item)"
                          >{{ item.issue_type }}</b-badge>
                        </div>
                      </div>
                      <div class="d-flex align-items-center flex-shrink-0 leaves-mobile-row__actions leaves-actions-cell">
                        <template v-if="isLeaveRow(item)">
                          <b-button
                            v-if="viewMode === 'my'"
                            size="sm"
                            variant="outline-primary"
                            @click="$emit('leave-click', item)"
                            aria-label="View details"
                          >
                            <feather-icon icon="EyeIcon" size="14" />
                          </b-button>
                          <template v-if="showLeaveApprovalFor(item)">
                            <b-button
                              size="sm"
                              variant="outline-success"
                              aria-label="Approve"
                              title="Approve"
                              @click="$emit('approve-leave', item)"
                            >
                              <feather-icon icon="CheckIcon" size="14" />
                            </b-button>
                            <b-button
                              size="sm"
                              variant="outline-danger"
                              aria-label="Reject"
                              title="Reject"
                              @click="$emit('reject-leave', item)"
                            >
                              <feather-icon icon="XIcon" size="14" />
                            </b-button>
                          </template>
                          <b-button
                            v-else-if="canCancelLeave(item)"
                            size="sm"
                            variant="outline-secondary"
                            class="leaves-mobile-cancel-btn"
                            @click="$emit('cancel-leave', item)"
                            aria-label="Cancel leave"
                            title="Cancel leave"
                          >
                            <feather-icon icon="MinusCircleIcon" size="14" />
                          </b-button>
                        </template>
                        <template v-else>
                          <template v-if="showRegApprovalFor(item)">
                            <b-button
                              size="sm"
                              variant="outline-success"
                              aria-label="Approve"
                              @click="$emit('approve-regularization', item)"
                            >
                              <feather-icon icon="CheckIcon" size="14" />
                            </b-button>
                            <b-button
                              size="sm"
                              variant="outline-danger"
                              aria-label="Reject"
                              @click="$emit('reject-regularization', item)"
                            >
                              <feather-icon icon="XIcon" size="14" />
                            </b-button>
                          </template>
                          <b-button
                            v-else-if="showRegularizeFor(item)"
                            size="sm"
                            variant="outline-primary"
                            @click="$emit('open-regularization-modal', item)"
                          >
                            Regularize
                          </b-button>
                        </template>
                      </div>
                    </div>
                  </b-list-group-item>
                </b-list-group>

                <div class="table-responsive table-scroll-wrapper leaves-table-wrap d-none d-lg-block">
                  <b-table
                    :items="displayedLeavesData"
                    :fields="leavesTabFields.length ? leavesTabFields : fields"
                    small
                    responsive
                    hover
                    class="mb-0 leaves-dense-table"
                  >
                    <template #cell(from_date)="data">
                      {{ data.item.from_date ? formatDate(data.item.from_date) : (data.item.datestring ? formatDate(data.item.datestring) : '-') }}
                    </template>
                    <template #cell(to_date)="data">
                      {{ data.item.to_date ? formatDate(data.item.to_date) : '-' }}
                    </template>
                    <template #cell(total_days)="data">
                      <span class="leaves-days-cell">{{ formatTotalDays(data.item.total_days) }}</span>
                    </template>
                    <template #cell(datestring)="data">
                      {{ formatDate(data.item.datestring) }}
                    </template>
                    <template #cell(employee)="data">
                      <div class="d-flex align-items-center leaves-emp-cell">
                        <span class="text-truncate leaves-emp-name">
                          {{ employeeLabel(data.item) }}
                        </span>
                        <b-badge
                          v-if="showLeaveBalanceHint && leaveBalanceValue(data.item) != null"
                          pill
                          class="leaves-bal-hint ml-50"
                        >{{ leaveBalanceValue(data.item) }}</b-badge>
                      </div>
                    </template>
                    <template #cell(issue_type)="data">
                      <span
                        v-if="isLeaveRow(data.item)"
                        class="leave-type-chip"
                      >{{ data.item.issue_type }}</span>
                      <b-badge
                        v-else
                        :variant="getIssueTypeVariant(data.item)"
                        class="reg-issue-badge"
                      >
                        {{ data.item.issue_type }}
                      </b-badge>
                    </template>
                    <template #cell(data_source_status)="data">
                      <data-source-badge :item="data.item" />
                    </template>
                    <template #cell(punch_in_time)="data">
                      <span v-if="data.item.punch_in_time">{{ formatTime(data.item.punch_in_time) }}</span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template #cell(punch_out_time)="data">
                      <span v-if="data.item.punch_out_time">{{ formatTime(data.item.punch_out_time) }}</span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template #cell(work_hours)="data">
                      {{ data.item.worktime || 'N/A' }}
                    </template>
                    <template #cell(status)="data">
                      <b-badge
                        pill
                        :variant="getApprovalStatusVariant(data.item.status || data.item.regularization_status)"
                        class="status-pill-badge"
                      >
                        {{ formatApprovalStatus(data.item.status || data.item.regularization_status) || 'Pending Manager' }}
                      </b-badge>
                    </template>
                    <template #cell(actions)="data">
                      <div class="d-inline-flex align-items-center leaves-actions-cell" style="gap: 0.25rem;">
                        <template v-if="isLeaveRow(data.item)">
                          <b-button
                            v-if="viewMode === 'my'"
                            size="sm"
                            variant="outline-primary"
                            @click="$emit('leave-click', data.item)"
                            aria-label="View details"
                          >
                            <feather-icon icon="EyeIcon" size="12" />
                          </b-button>
                          <div v-if="showLeaveApprovalFor(data.item)" class="d-flex align-items-center" style="gap: 0.25rem;">
                            <b-button size="sm" variant="outline-success" aria-label="Approve" @click="$emit('approve-leave', data.item)">
                              <feather-icon icon="CheckIcon" size="12" />
                            </b-button>
                            <b-button size="sm" variant="outline-danger" aria-label="Reject" @click="$emit('reject-leave', data.item)">
                              <feather-icon icon="XIcon" size="12" />
                            </b-button>
                          </div>
                          <b-button
                            v-else-if="canCancelLeave(data.item)"
                            size="sm"
                            variant="outline-danger"
                            @click="$emit('cancel-leave', data.item)"
                            aria-label="Cancel leave"
                          >
                            <feather-icon icon="XIcon" size="12" />
                          </b-button>
                          <span v-else-if="viewMode !== 'my'" class="text-muted">-</span>
                        </template>
                        <template v-else>
                          <div v-if="showRegApprovalFor(data.item)" class="d-flex align-items-center" style="gap: 0.25rem;">
                            <b-button size="sm" variant="outline-success" aria-label="Approve" @click="$emit('approve-regularization', data.item)">
                              <feather-icon icon="CheckIcon" size="12" />
                            </b-button>
                            <b-button size="sm" variant="outline-danger" aria-label="Reject" @click="$emit('reject-regularization', data.item)">
                              <feather-icon icon="XIcon" size="12" />
                            </b-button>
                          </div>
                          <b-button v-else-if="showRegularizeFor(data.item)" size="sm" variant="outline-primary" @click="$emit('open-regularization-modal', data.item)">
                            Regularize
                          </b-button>
                          <span v-else class="text-muted">-</span>
                        </template>
                      </div>
                    </template>
                  </b-table>
                </div>
              </template>
              <div v-else class="text-center text-muted py-4 leaves-empty-state">
                <feather-icon icon="FileTextIcon" size="28" class="mb-1" />
                <p class="mb-0 small">No leave records found</p>
              </div>
            </b-tab>
            
            <!-- Regularization Tab -->
            <b-tab>
              <template #title>
                Regularization 
                <b-badge v-if="regularizationData.length > 0" variant="warning" pill class="ml-1 leaves-count-badge">{{ regularizationData.length }}</b-badge>
              </template>
              
              <template v-if="regularizationData.length > 0">
                <b-list-group
                  flush
                  class="d-lg-none leaves-mobile-list-group border-0 bg-transparent"
                >
                  <b-list-group-item
                    v-for="(item, idx) in displayedRegularizationData"
                    :key="regMobileKey(item, idx)"
                    class="leaves-mobile-row px-2 py-1"
                  >
                    <div class="d-flex align-items-center justify-content-between leaves-mobile-row__inner">
                      <div class="leaves-mobile-row__main min-w-0 flex-grow-1 mr-2">
                        <div
                          v-if="viewMode === 'team' || viewMode === 'org'"
                          class="leaves-mobile-row__name text-truncate small font-weight-bold text-body mb-0"
                        >
                          {{ regMobilePrimary(item) }}
                        </div>
                        <div class="d-flex align-items-center flex-wrap leaves-mobile-row__meta">
                          <span
                            class="leaves-mobile-row__date small text-body"
                            :class="{ 'font-weight-bold': viewMode === 'my' }"
                          >{{ regDateLabel(item) }}</span>
                          <b-badge
                            variant="light"
                            class="reg-issue-chip ml-1"
                            :class="'reg-issue--' + getIssueTypeVariant(item)"
                          >{{ item.issue_type }}</b-badge>
                        </div>
                      </div>
                      <div class="d-flex align-items-center flex-shrink-0 leaves-mobile-row__actions leaves-actions-cell">
                        <template v-if="showRegApprovalFor(item)">
                          <b-button
                            size="sm"
                            variant="outline-success"
                            aria-label="Approve"
                            @click="$emit('approve-regularization', item)"
                          >
                            <feather-icon icon="CheckIcon" size="14" />
                          </b-button>
                          <b-button
                            size="sm"
                            variant="outline-danger"
                            aria-label="Reject"
                            @click="$emit('reject-regularization', item)"
                          >
                            <feather-icon icon="XIcon" size="14" />
                          </b-button>
                        </template>
                        <b-button
                          v-else-if="showRegularizeFor(item)"
                          size="sm"
                          variant="outline-primary"
                          @click="$emit('open-regularization-modal', item)"
                        >
                          Regularize
                        </b-button>
                      </div>
                    </div>
                  </b-list-group-item>
                </b-list-group>

                <div class="table-responsive table-scroll-wrapper leaves-table-wrap d-none d-lg-block">
                  <b-table
                    :items="displayedRegularizationData"
                    :fields="fields"
                    small
                    responsive
                    hover
                    class="mb-0 leaves-dense-table"
                  >
                    <template #cell(datestring)="data">
                      {{ formatDate(data.item.datestring) }}
                    </template>
                    <template #cell(employee)="data">
                      {{ data.item.employee_name || getUserName(data.item.emp_id) || data.item.emp_id || 'N/A' }}
                    </template>
                    <template #cell(issue_type)="data">
                      <span class="reg-issue-chip" :class="'reg-issue--' + getIssueTypeVariant(data.item)">
                        {{ data.item.issue_type }}
                      </span>
                    </template>
                    <template #cell(data_source_status)="data">
                      <data-source-badge :item="data.item" muted />
                    </template>
                    <template #cell(punch_in_time)="data">
                      <span v-if="data.item.punch_in_time">{{ formatTime(data.item.punch_in_time) }}</span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template #cell(punch_out_time)="data">
                      <span v-if="data.item.punch_out_time">{{ formatTime(data.item.punch_out_time) }}</span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template #cell(intime)="data">
                      <span v-if="data.item.intime && data.item.intime !== '0:00:00' && data.item.intime !== '00:00:00'">{{ formatTime(data.item.intime) }}</span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template #cell(outtime)="data">
                      <span v-if="data.item.outtime && data.item.outtime !== '0:00:00' && data.item.outtime !== '00:00:00'">{{ formatTime(data.item.outtime) }}</span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template #cell(work_hours)="data">
                      {{ data.item.worktime || 'N/A' }}
                    </template>
                    <template #cell(status)="data">
                      <b-badge
                        pill
                        :variant="getApprovalStatusVariant(data.item.status || data.item.regularization_status)"
                        class="status-pill-badge"
                      >
                        {{ formatApprovalStatus(data.item.status || data.item.regularization_status) || 'Pending Manager' }}
                      </b-badge>
                    </template>
                    <template #cell(actions)="data">
                      <div class="d-inline-flex align-items-center leaves-actions-cell" style="gap: 0.25rem;">
                        <div v-if="isLeaveRow(data.item) && showLeaveApprovalFor(data.item)" class="d-flex align-items-center" style="gap: 0.25rem;">
                          <b-button size="sm" variant="outline-success" aria-label="Approve" @click="$emit('approve-leave', data.item)">
                            <feather-icon icon="CheckIcon" size="12" />
                          </b-button>
                          <b-button size="sm" variant="outline-danger" aria-label="Reject" @click="$emit('reject-leave', data.item)">
                            <feather-icon icon="XIcon" size="12" />
                          </b-button>
                        </div>
                        <div v-else-if="!isLeaveRow(data.item) && showRegApprovalFor(data.item)" class="d-flex align-items-center" style="gap: 0.25rem;">
                          <b-button size="sm" variant="outline-success" aria-label="Approve" @click="$emit('approve-regularization', data.item)">
                            <feather-icon icon="CheckIcon" size="12" />
                          </b-button>
                          <b-button size="sm" variant="outline-danger" aria-label="Reject" @click="$emit('reject-regularization', data.item)">
                            <feather-icon icon="XIcon" size="12" />
                          </b-button>
                        </div>
                        <b-button v-else-if="!isLeaveRow(data.item) && showRegularizeFor(data.item)" size="sm" variant="outline-primary" @click="$emit('open-regularization-modal', data.item)">
                          Regularize
                        </b-button>
                        <span v-else class="text-muted">-</span>
                      </div>
                    </template>
                  </b-table>
                </div>
              </template>
              <div v-else class="text-center text-muted py-4 leaves-empty-state">
                <feather-icon icon="ClockIcon" size="28" class="mb-1" />
                <p class="mb-0 small">No regularization records found</p>
              </div>
            </b-tab>
          </b-tabs>
        </b-card-body>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BButton,
  BBadge,
  BTable,
  BTabs,
  BTab,
  BDropdown,
  BDropdownItem,
  BListGroup,
  BListGroupItem,
} from 'bootstrap-vue'
import { isWFHLeaveRecord } from '@/utils/attendance-leave-types'

const DataSourceBadge = {
  name: 'DataSourceBadge',
  functional: true,
  props: { item: Object, muted: { type: Boolean, default: false } },
  render(h, { props }) {
    const { item, muted } = props
    const isWFH = isWFHLeaveRecord(item) || item.issue_type === 'WFH' || String(item.issue_type || '').includes('WFH')
    
    if (item.source_type === 'Leave' && isWFH) {
      return h('span', { class: 'data-source-muted' }, 'WFH')
    }
    if (item.source_type === 'Leave') {
      return h('span', { class: 'data-source-muted' }, 'Leave')
    }
    if (muted) {
      return h('div', { class: 'd-flex align-items-center data-source-row', style: 'gap: 0.2rem;' }, [
        h('span', {
          class: ['data-src-chip', item.has_punch_data ? 'data-src-ok' : 'data-src-miss'],
        }, `PD ${item.has_punch_data ? '✓' : '✗'}`),
        h('span', {
          class: [
            'data-src-chip',
            item.has_biometric ? 'data-src-ok' : (isWFH ? 'data-src-na' : 'data-src-miss'),
          ],
        }, `BD ${item.has_biometric ? '✓' : (isWFH ? 'N/A' : '✗')}`),
      ])
    }
    return h('div', { class: 'd-flex align-items-center data-source-row', style: 'gap: 0.2rem;' }, [
      h('b-badge', { 
        props: { variant: item.has_punch_data ? 'success' : 'danger' },
      }, `PD ${item.has_punch_data ? '✓' : '✗'}`),
      h('b-badge', { 
        props: { variant: item.has_biometric ? 'success' : (isWFH ? 'secondary' : 'danger') },
      }, `BD ${item.has_biometric ? '✓' : (isWFH ? 'N/A' : '✗')}`),
    ])
  }
}

export default {
  name: 'LeavesRegularizationTabs',
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,
    BBadge,
    BTable,
    BTabs,
    BTab,
    BDropdown,
    BDropdownItem,
    BListGroup,
    BListGroupItem,
    DataSourceBadge,
  },
  props: {
    activeTab: { type: Number, default: 0 },
    leavesData: { type: Array, default: () => [] },
    regularizationData: { type: Array, default: () => [] },
    displayedLeavesData: { type: Array, default: () => [] },
    displayedRegularizationData: { type: Array, default: () => [] },
    displayedLeavesCount: { type: Number, default: 10 },
    displayedRegularizationCount: { type: Number, default: 10 },
    displayLimit: { type: Number, default: 10 },
    fields: { type: Array, default: () => [] },
    leavesTabFields: { type: Array, default: () => [] },
    viewMode: { type: String, default: 'my' },
    showDownloadButton: { type: Boolean, default: false },
    showLeavesDownloadButton: { type: Boolean, default: false },
    hasActiveFilters: { type: Boolean, default: false },
    /** 'all' | 'pending' | 'approved' | 'rejected' — from Review all / pending filter */
    statusFilter: { type: String, default: 'all' },
    canManageLeaves: { type: Boolean, default: false },
    canApproveLeaveAllStages: { type: Boolean, default: false },
    /** When provided, used to decide if current user can approve this leave (table + detail); overrides inline manager/all-stages check */
    canApproveLeave: { type: Function, default: null },
    canApproveRegularizationAllStages: { type: Boolean, default: false },
    canApplyRegularization: { type: Boolean, default: false },
    getUserName: { type: Function, required: true },
    formatDate: { type: Function, required: true },
    formatTime: { type: Function, required: true },
    getApprovalStatusVariant: { type: Function, required: true },
    formatApprovalStatus: { type: Function, required: true },
    getIssueVariant: { type: Function, required: true },
    isManagerOfEmployee: { type: Function, required: true },
    needsLeaveApproval: { type: Function, required: true },
    isLeavePendingManagerApproval: { type: Function, required: true },
    canCancelLeave: { type: Function, default: () => false },
    isRegularizationRaised: { type: Function, required: true },
    needsManagerApproval: { type: Function, required: true },
    /** Optional: (empId) => number|string|null — compact leave balance from existing team data */
    getEmployeeLeaveBalance: { type: Function, default: null },
  },
  computed: {
    showLeaveBalanceHint() {
      return (this.viewMode === 'team' || this.viewMode === 'org') && typeof this.getEmployeeLeaveBalance === 'function'
    },
  },
  methods: {
    employeeLabel(item) {
      return item.employee_name || this.getUserName(item.emp_id) || item.emp_id || 'N/A'
    },
    leaveMobileKey(item, idx) {
      return `leave-m-${item.id || item.emp_id || 'x'}-${item.from_date || item.datestring || idx}`
    },
    regMobileKey(item, idx) {
      return `reg-m-${item.id || item.emp_id || 'x'}-${item.datestring || idx}`
    },
    leaveMobilePrimary(item) {
      if (this.viewMode === 'team' || this.viewMode === 'org') {
        return this.employeeLabel(item)
      }
      // Employee view: date range is the primary line; type sits below
      return this.leaveDateRange(item)
    },
    regMobilePrimary(item) {
      if (this.viewMode === 'team' || this.viewMode === 'org') {
        return this.employeeLabel(item)
      }
      return this.regDateLabel(item)
    },
    leaveDateRange(item) {
      const fromRaw = item.from_date || item.datestring
      const toRaw = item.to_date
      const from = fromRaw ? this.formatDate(fromRaw) : '-'
      const to = toRaw ? this.formatDate(toRaw) : from
      if (!toRaw || from === to) return from
      return `${from} – ${to}`
    },
    regDateLabel(item) {
      return item.datestring ? this.formatDate(item.datestring) : '-'
    },
    formatTotalDays(totalDays) {
      if (totalDays == null) return '-'
      const n = parseFloat(totalDays)
      if (Number.isNaN(n)) return '-'
      return n === 1 ? '1d' : `${n}d`
    },
    leaveBalanceValue(item) {
      if (!item || typeof this.getEmployeeLeaveBalance !== 'function') return null
      const val = this.getEmployeeLeaveBalance(item.emp_id)
      if (val == null || val === '') return null
      if (typeof val === 'object') {
        const bal = val.balance != null ? val.balance : val.leaveBalance
        return bal != null ? bal : null
      }
      return val
    },
    leaveBalanceTooltip(item) {
      if (!item || typeof this.getEmployeeLeaveBalance !== 'function') return 'Leave balance'
      const val = this.getEmployeeLeaveBalance(item.emp_id)
      if (val == null) return 'Leave balance'
      if (typeof val === 'object') {
        const bal = val.balance != null ? val.balance : val.leaveBalance
        const taken = val.taken != null ? val.taken : val.leavesApplied
        const parts = [`Balance: ${bal != null ? bal : '—'}`]
        if (taken != null) parts.push(`Taken: ${taken}`)
        return parts.join(' · ')
      }
      return `Leave balance: ${val}`
    },
    getIssueTypeVariant(item) {
      const isWFH = isWFHLeaveRecord(item) || item.issue_type === 'WFH' || String(item.issue_type || '').includes('WFH')
      if (isWFH) return 'primary'
      if (item.source_type === 'Leave') return 'info'
      return this.getIssueVariant(item.issue_type)
    },
    isLeaveRow(item) {
      return item && item.source_type === 'Leave'
    },
    showLeaveApprovalFor(item) {
      if (!item || !this.isLeaveRow(item)) return false
      if (typeof this.needsLeaveApproval !== 'function' || !this.needsLeaveApproval(item)) return false
      if (typeof this.canApproveLeave === 'function' && this.canApproveLeave(item)) return true
      if (this.canApproveLeaveAllStages) return true
      if (this.viewMode !== 'team' && this.viewMode !== 'org') return false
      if (!this.canManageLeaves) return false
      return typeof this.isManagerOfEmployee === 'function' && this.isManagerOfEmployee(item.emp_id) && typeof this.isLeavePendingManagerApproval === 'function' && this.isLeavePendingManagerApproval(item)
    },
    showRegApprovalFor(item) {
      if (!item || this.isLeaveRow(item) || !this.canManageLeaves) return false
      const raised = typeof this.isRegularizationRaised === 'function' ? this.isRegularizationRaised(item) : false
      if (!raised) return false
      if (this.canApproveRegularizationAllStages) return true
      if (this.viewMode !== 'team' && this.viewMode !== 'org') return false
      return typeof this.isManagerOfEmployee === 'function' && this.isManagerOfEmployee(item.emp_id) && typeof this.needsManagerApproval === 'function' && this.needsManagerApproval(item)
    },
    showRegularizeFor(item) {
      if (!item || this.isLeaveRow(item) || !this.canApplyRegularization) return false
      const raised = typeof this.isRegularizationRaised === 'function' ? this.isRegularizationRaised(item) : false
      return !raised
    },
  },
}
</script>

<style scoped>
/* Compact section header / toolbar row */
.leaves-reg-card {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: #ffffff !important;
  border: 1px solid #e8ecf0;
}

.leaves-reg-card >>> .card-body {
  padding: 0.3rem 0.55rem 0.4rem;
  background: #ffffff !important;
}

.leaves-reg-header {
  padding-top: 0;
  padding-bottom: 0.2rem;
  margin-bottom: 0.2rem;
  min-height: 0;
}

.leaves-reg-title {
  font-size: 0.9rem;
  line-height: 1.25;
}

.leaves-reg-toolbar .btn {
  padding-top: 0.2rem;
  padding-bottom: 0.2rem;
}

/* Pill tabs — sober light selected state (not solid teal) */
.leaves-reg-card >>> .leaves-reg-tabs {
  flex-wrap: nowrap;
  gap: 0.2rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 0.25rem !important;
  border-bottom: none;
  background: #f3f5f7 !important;
  padding: 0.15rem !important;
}

.leaves-reg-card >>> .leaves-reg-tabs .nav-item {
  flex: 1 1 0;
  min-width: 0;
  margin-bottom: 0;
}

.leaves-reg-card >>> .leaves-reg-tabs .nav-link,
.leaves-reg-card >>> .nav-pills .nav-link {
  font-size: 0.78rem !important;
  font-weight: 600;
  padding: 0.3rem 0.45rem !important;
  text-align: center;
  white-space: nowrap;
  border-radius: 4px !important;
  color: #374151 !important;
  line-height: 1.2;
  min-height: 0;
  border: none !important;
  background: transparent !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.leaves-reg-card >>> .leaves-reg-tabs .nav-link.active,
.leaves-reg-card >>> .nav-pills .nav-link.active {
  background-color: #fff !important;
  color: #007a8a !important;
  border: 1px solid #e8ecf0 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.leaves-reg-card >>> .leaves-reg-tabs .nav-link.active .leaves-count-badge,
.leaves-reg-card >>> .nav-pills .nav-link.active .leaves-count-badge {
  background: rgba(0, 153, 172, 0.18) !important;
  color: #007a8a !important;
}

.leaves-reg-card >>> .leaves-reg-tabs .nav-link.active .leaves-pending-chip {
  background: #fff8e6 !important;
  color: #9a7b0a !important;
}

.table-scroll-wrapper {
  max-height: 400px;
  overflow-y: auto;
}

.leaves-table-wrap >>> .table.b-table > tbody > tr > td,
.leaves-table-wrap >>> .table.b-table > thead > tr > th {
  padding: 0.3rem 0.45rem;
  vertical-align: middle;
  font-size: 0.8rem;
}

.leaves-table-wrap >>> .table.b-table > thead > tr > th {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #6e6b7b;
  white-space: nowrap;
  border-top: none;
  background: #fafbfc;
}

.leaves-table-wrap >>> .table.b-table > tbody > tr > td .btn-sm {
  padding: 0.15rem 0.35rem;
  line-height: 1.2;
}

.leaves-actions-cell .btn {
  min-width: 1.65rem;
  border-radius: 6px;
  border: none !important;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.leaves-actions-cell .btn-outline-primary {
  background: #f0fafb !important;
  border: none !important;
  color: #007a8a !important;
  box-shadow: 0 1px 2px rgba(0, 153, 172, 0.08);
}

.leaves-actions-cell .btn-outline-success {
  background: #eef8f0 !important;
  border: none !important;
  color: #1f9d57 !important;
  box-shadow: 0 1px 2px rgba(40, 199, 111, 0.08);
}

.leaves-actions-cell .btn-outline-danger {
  background: #fdf2f2 !important;
  border: none !important;
  color: #c0392b !important;
  box-shadow: 0 1px 2px rgba(234, 84, 85, 0.08);
}

.leaves-reg-toolbar .btn,
.leaves-toolbar-icon-btn,
.leaves-filters-btn {
  border-radius: 7px;
  font-weight: 500;
  border: none !important;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.leaves-reg-toolbar .btn-outline-primary,
.leaves-filters-btn {
  background: #f0fafb !important;
  border: none !important;
  color: #007a8a !important;
  box-shadow: 0 1px 2px rgba(0, 153, 172, 0.08);
}

.leaves-filters-btn--active {
  background: rgba(0, 153, 172, 0.14) !important;
  border: none !important;
  color: #007a8a !important;
}

.leaves-reg-toolbar .btn-outline-secondary {
  background: #f3f5f7 !important;
  border: none !important;
  color: #5e6e82 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.leaves-reg-toolbar .btn-outline-success {
  background: #eef8f0 !important;
  border: none !important;
  color: #1f9d57 !important;
  box-shadow: 0 1px 2px rgba(40, 199, 111, 0.08);
}

.leaves-emp-cell {
  min-width: 0;
  max-width: 11rem;
}

.leaves-emp-name {
  max-width: 8.5rem;
}

.leaves-bal-hint {
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 600;
  background: #eef3f8 !important;
  color: #5e6e82 !important;
  border: 1px solid #d8e0ea;
  padding: 0.15em 0.45em;
}

.leave-type-chip {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #5e6e82;
  background: #f3f5f7;
  border: 1px solid #e5e9ef;
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
  white-space: nowrap;
  max-width: 7.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.reg-issue-chip {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 500;
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
  white-space: nowrap;
  max-width: 8.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  background: #f3f5f7;
  color: #5e6e82;
  border: 1px solid #e5e9ef;
}

.reg-issue--danger {
  background: #fdf2f2;
  color: #c0392b;
  border-color: #f5c6cb;
}

.reg-issue--warning {
  background: #fff8e6;
  color: #9a7b0a;
  border-color: #f0e0a8;
}

.reg-issue--info {
  background: #eef5fb;
  color: #3b6d99;
  border-color: #cfe0f0;
}

.reg-issue--success {
  background: #eef8f0;
  color: #2d7a46;
  border-color: #c8e6d0;
}

.status-pill-badge {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 0.28em 0.55em;
}

.leaves-days-cell {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.leaves-count-badge,
.leaves-pending-chip {
  font-size: 0.65rem;
  font-weight: 600;
  vertical-align: middle;
}

.leaves-empty-state {
  opacity: 0.85;
}

/* Deep: DataSourceBadge is a child functional component */
.leaves-table-wrap >>> .data-source-muted {
  font-size: 0.72rem;
  color: #8a8d93;
  font-weight: 500;
}

.leaves-table-wrap >>> .data-source-row {
  flex-wrap: nowrap;
}

.leaves-table-wrap >>> .data-src-chip {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 600;
  border-radius: 3px;
  padding: 0.1rem 0.3rem;
  white-space: nowrap;
  border: 1px solid transparent;
}

.leaves-table-wrap >>> .data-src-ok {
  background: #eef8f0;
  color: #2d7a46;
  border-color: #c8e6d0;
}

.leaves-table-wrap >>> .data-src-miss {
  background: #fdf2f2;
  color: #c0392b;
  border-color: #f5c6cb;
}

.leaves-table-wrap >>> .data-src-na {
  background: #f3f5f7;
  color: #8a8d93;
  border-color: #e5e9ef;
}

/* Mobile list — compact full-width row cards */
.leaves-mobile-list-group {
  max-height: 420px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff !important;
  border: 1px solid #e8ecf0 !important;
  border-radius: 6px;
}

.leaves-mobile-row {
  background: #fff !important;
  border: none !important;
  border-bottom: 1px solid #e8ecf0 !important;
  position: static !important;
  height: auto !important;
  min-height: auto !important;
  padding-top: 0.35rem !important;
  padding-bottom: 0.35rem !important;
  margin-bottom: 0 !important;
  border-radius: 0 !important;
  overflow: visible !important;
}

.leaves-mobile-row:last-child {
  margin-bottom: 0 !important;
  border-bottom: none !important;
}

.leaves-mobile-row__inner {
  gap: 0.25rem;
}

.leaves-mobile-row__meta {
  gap: 0.25rem;
  row-gap: 0.1rem;
  min-width: 0;
}

.leaves-mobile-row__date {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.leaves-mobile-row__actions {
  gap: 0.3rem;
  flex-wrap: nowrap;
  justify-content: flex-end;
}

.leaves-mobile-row__actions .btn {
  margin: 0 !important;
  padding: 0.2rem 0.4rem;
  flex-shrink: 0;
}

.leaves-mobile-cancel-btn {
  background: #f3f5f7 !important;
  border: 1px solid #e5e9ef !important;
  color: #5e6e82 !important;
  box-shadow: none !important;
}

.leaves-mobile-list-group >>> .list-group-item {
  position: static !important;
  height: auto !important;
  min-height: auto !important;
  background-color: #fff !important;
}

.leaves-download-dropdown >>> .dropdown-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
  background: #eef8f0 !important;
  border: none !important;
  color: #1f9d57 !important;
  box-shadow: 0 1px 2px rgba(40, 199, 111, 0.08);
  border-radius: 7px;
}

@media (max-width: 991px) {
  .table-scroll-wrapper,
  .leaves-table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }

  .leaves-reg-card:not(.leaves-reg-card--employee-chrome) {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    overflow: visible !important;
  }

  .leaves-reg-root--employee.row {
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .leaves-reg-root--employee > [class*='col'] {
    padding-left: 0 !important;
    padding-right: 0 !important;
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  .leaves-reg-card--employee-chrome {
    background: #ffffff !important;
    border: 1px solid #e8ecf0 !important;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04) !important;
    border-radius: 10px !important;
    overflow: hidden !important;
  }

  .leaves-reg-card:not(.leaves-reg-card--employee-chrome) >>> .card-body {
    padding: 0.25rem 0.4rem 0.35rem !important;
    background: transparent !important;
  }

  .leaves-reg-card--employee-chrome >>> .card-body {
    padding: 0.55rem 0.6rem !important;
    background: #ffffff !important;
  }

  .leaves-reg-card >>> .leaves-reg-tab-content,
  .leaves-reg-card >>> .leaves-reg-tab-content .tab-content {
    display: block !important;
    flex: none !important;
    height: auto !important;
    min-height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    background: transparent !important;
  }

  .leaves-reg-card >>> .leaves-reg-tab-content .tab-pane {
    display: none !important;
    flex: none !important;
    height: auto !important;
    min-height: auto !important;
    max-height: none !important;
    overflow: visible !important;
  }

  .leaves-reg-card >>> .leaves-reg-tab-content .tab-pane.active {
    display: block !important;
  }

  .leaves-reg-card >>> .leaves-reg-tabs {
    padding: 0.15rem !important;
    margin-bottom: 0.25rem !important;
    background: #f3f5f7 !important;
  }

  .leaves-reg-card >>> .leaves-reg-tabs .nav-link {
    font-size: 0.74rem !important;
    padding: 0.3rem 0.35rem !important;
  }

  .leaves-reg-header {
    padding-top: 0 !important;
    padding-bottom: 0.2rem !important;
    margin-bottom: 0.2rem !important;
  }

  .leaves-reg-toolbar {
    gap: 0.3rem;
  }

  .leaves-reg-toolbar .btn,
  .leaves-toolbar-icon-btn,
  .leaves-filters-btn {
    min-width: 2rem;
    min-height: 2rem;
  }

  .leaves-mobile-list-group {
    max-height: 420px;
    background: #fff !important;
  }

  .leaves-mobile-row {
    margin-bottom: 0 !important;
    overflow: visible !important;
  }

  .leaves-mobile-row__inner {
    flex-direction: row;
    align-items: center;
    gap: 0.25rem;
  }

  .leaves-mobile-row__main {
    margin-right: 0.25rem !important;
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
  }

  .leaves-mobile-row__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .leaves-mobile-row__meta {
    flex-wrap: nowrap;
    overflow: hidden;
    min-width: 0;
  }

  .leaves-mobile-row__actions {
    width: auto;
    flex-shrink: 0;
    flex-wrap: nowrap;
    padding-top: 0;
    border-top: none;
    gap: 0.2rem;
  }

  .leaves-mobile-row__actions .btn {
    padding: 0.15rem 0.3rem;
    min-width: 1.75rem;
    min-height: 1.75rem;
  }

  .leaves-mobile-row__name + .leaves-mobile-row__meta {
    margin-top: 0.05rem;
  }

  .leaves-mobile-row .leaves-actions-cell .btn-outline-primary {
    background: #fff !important;
    border: 1px solid #e5e9ef !important;
    box-shadow: none !important;
  }

  .leaves-actions-cell .btn {
    min-width: 1.75rem;
    min-height: 1.75rem;
  }

  .leaves-dense-table.table.b-table,
  .leaves-table-wrap >>> .table.b-table {
    min-width: 640px;
    max-width: none;
  }
}

@media (max-width: 767px) {
  .leaves-reg-header {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 0.35rem;
  }

  .leaves-reg-title {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 0.82rem;
  }

  .leaves-reg-toolbar {
    width: auto;
    flex: 0 0 auto;
    flex-wrap: nowrap;
    justify-content: flex-end;
  }

  .leaves-reg-toolbar .btn,
  .leaves-toolbar-icon-btn {
    min-width: 2.1rem;
    min-height: 2.1rem;
    padding-left: 0.4rem;
    padding-right: 0.4rem;
    flex: 0 0 auto;
  }

  .leaves-reg-card >>> .card-body {
    padding: 0.2rem 0.35rem 0.3rem !important;
  }

  .leaves-emp-cell {
    max-width: 9rem;
  }

  .leaves-emp-name {
    max-width: 6.5rem;
  }
}

@media (max-width: 575px) {
  .leaves-reg-title {
    font-size: 0.8rem;
  }

  .leaves-reg-card >>> .card-body {
    padding: 0.15rem 0.3rem 0.25rem !important;
  }

  .leaves-actions-cell .btn {
    min-width: 2rem;
    min-height: 2rem;
  }

  .leaves-table-wrap >>> .table.b-table > tbody > tr > td,
  .leaves-table-wrap >>> .table.b-table > thead > tr > th {
    font-size: 0.74rem;
    padding: 0.28rem 0.35rem;
  }
}
</style>
